import { ENERGIES, PERIODS, USAGES, isValidRatio } from './domain';
import type { EnergyKey, RatioTable, UsageKey } from './types';

/** Une ligne de la feuille : un couple typologie × poste. */
export interface SheetRow {
  u: UsageKey; e: EnergyKey; usageLabel: string; energyLabel: string; icon: string; first: boolean;
}

export const SHEET_ROWS: SheetRow[] = USAGES.flatMap((u) =>
  ENERGIES.map((e, i) => ({
    u: u.key, e: e.key, usageLabel: u.label, energyLabel: e.short, icon: e.icon, first: i === 0,
  })));

export const SHEET_COLS = PERIODS;

export interface Cell { r: number; c: number }
export interface Rect { r1: number; c1: number; r2: number; c2: number }

export const rectOf = (a: Cell, b: Cell): Rect => ({
  r1: Math.min(a.r, b.r), r2: Math.max(a.r, b.r), c1: Math.min(a.c, b.c), c2: Math.max(a.c, b.c),
});
export const inRect = (rect: Rect, r: number, c: number) => r >= rect.r1 && r <= rect.r2 && c >= rect.c1 && c <= rect.c2;
export const rectSize = (rect: Rect) => (rect.r2 - rect.r1 + 1) * (rect.c2 - rect.c1 + 1);
export const clamp = (v: number, max: number) => Math.max(0, Math.min(max, v));

export const cellKey = (r: number, c: number) => `${r}:${c}`;
export const cellValue = (t: RatioTable, r: number, c: number): number =>
  t[SHEET_ROWS[r].u][SHEET_COLS[c].key][SHEET_ROWS[r].e];

export function setCell(t: RatioTable, r: number, c: number, v: number): RatioTable {
  const { u, e } = SHEET_ROWS[r];
  const p = SHEET_COLS[c].key;
  return { ...t, [u]: { ...t[u], [p]: { ...t[u][p], [e]: v } } };
}

/** Nombre saisi à la française ou à l'anglaise, espaces fines et insécables tolérées. */
export const parseNum = (s: string): number => {
  const clean = s.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  return clean === '' ? NaN : Number(clean);
};

/** Découpe un presse-papiers tableur (tabulations, points-virgules, retours ligne). */
export function parseClipboard(text: string): string[][] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  while (lines.length > 1 && lines[lines.length - 1].trim() === '') lines.pop();
  return lines.map((l) => (l.includes('\t') ? l.split('\t') : l.split(';')));
}

/** Colle une matrice à partir d'une cellule : renvoie la table et le compte appliqué/ignoré. */
export function applyMatrix(t: RatioTable, at: Cell, matrix: string[][]) {
  let next = t;
  let applied = 0;
  let skipped = 0;
  matrix.forEach((line, dr) => line.forEach((raw, dc) => {
    const r = at.r + dr;
    const c = at.c + dc;
    if (r >= SHEET_ROWS.length || c >= SHEET_COLS.length) return;
    if (raw.trim() === '') return;
    const n = parseNum(raw);
    if (!isValidRatio(n)) { skipped += 1; return; }
    next = setCell(next, r, c, Math.round(n * 10) / 10);
    applied += 1;
  }));
  return { next, applied, skipped };
}

/** Sérialise une sélection au format tableur (tabulations). */
export const serializeRect = (t: RatioTable, rect: Rect): string => {
  const lines: string[] = [];
  for (let r = rect.r1; r <= rect.r2; r += 1) {
    const cells: string[] = [];
    for (let c = rect.c1; c <= rect.c2; c += 1) cells.push(String(cellValue(t, r, c)));
    lines.push(cells.join('\t'));
  }
  return lines.join('\n');
};

/** Rebase les ratios : les cellules restées au défaut suivent le nouveau défaut, les autres sont conservées. */
export function remapRatios(cur: RatioTable, prevBase: RatioTable, nextBase: RatioTable): RatioTable {
  let next = cur;
  for (let r = 0; r < SHEET_ROWS.length; r += 1) {
    for (let c = 0; c < SHEET_COLS.length; c += 1) {
      if (cellValue(cur, r, c) === cellValue(prevBase, r, c)) next = setCell(next, r, c, cellValue(nextBase, r, c));
    }
  }
  return next;
}

/** Remet les valeurs d'une sélection à leur défaut. */
export function resetRect(t: RatioTable, defaults: RatioTable, rect: Rect): RatioTable {
  let next = t;
  for (let r = rect.r1; r <= rect.r2; r += 1) {
    for (let c = rect.c1; c <= rect.c2; c += 1) next = setCell(next, r, c, cellValue(defaults, r, c));
  }
  return next;
}
